import React, { useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
import type { Button } from "./Button";

type ButtonProps = React.ComponentProps<typeof Button>;

interface ButtonGroupProps {
  children:
    | React.ReactElement<ButtonProps>
    | Array<React.ReactElement<ButtonProps> | null | false | undefined>;
  className?: string;
  style?: React.CSSProperties;
  /**
   * By default each child gets `boxShadow: none` so segmented buttons sit flush.
   * Set false when children need inset shadow (e.g. pressed state on button radios).
   */
  stripChildBoxShadow?: boolean;
}

const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  className,
  style,
  stripChildBoxShadow = true,
}) => {
  const childCount = React.Children.count(children);

  const divRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!divRef.current) return;

    const children = divRef.current.children;
    if (!children.length) return;
    const removeBorders = !className?.includes("divide");

    for (let index = 0; index < children.length; index++) {
      const child = children[index] as HTMLElement;
      if (stripChildBoxShadow) {
        child.style.boxShadow = "none";
      }

      if (index === 0) {
        if (removeBorders) child.style.borderRight = "none";
        child.style.borderTopRightRadius = "0";
        child.style.borderBottomRightRadius = "0";
      } else if (index === children.length - 1) {
        if (removeBorders) child.style.borderLeft = "none";
        child.style.borderTopLeftRadius = "0";
        child.style.borderBottomLeftRadius = "0";
      } else {
        if (removeBorders) {
          child.style.borderLeft = "none";
          child.style.borderRight = "none";
        }
        child.style.borderTopLeftRadius = "0";
        child.style.borderBottomLeftRadius = "0";
        child.style.borderTopRightRadius = "0";
        child.style.borderBottomRightRadius = "0";
      }
    }
  });

  return (
    <div ref={divRef} className={twMerge("grid grid-flow-col", className)} style={style}>
      {React.Children.map(children, (child, index) => {
        if (!child) return null;

        if (childCount === 1) {
          // Do not modify the rounded if there is only one child
          return React.cloneElement(child as React.ReactElement);
        }

        if (index === 0) {
          // Round the left side of the first button
          return React.cloneElement(child, {
            groupPosition: "left",
          });
        }
        if (index === childCount - 1) {
          // Round the right side of the last button
          return React.cloneElement(child, {
            groupPosition: "right",
          });
        }
        // Do not round the middle buttons
        return React.cloneElement(child, {
          groupPosition: "middle",
        });
      })}
    </div>
  );
};

export { ButtonGroup };
